/**
 * Admin dashboard, ordered by what needs doing rather than what looks good:
 * quick actions → pending requests → dues to chase → next events → safety.
 * The actions sit first because logging a result or adding a member is what
 * the committee opens this page to do; everything below is what to react to.
 *
 * The hero counts the club's own distance — the kilometres it has PUT ON, one
 * completed event at a time. It is the one flourish.
 */

import { useNavigate } from 'react-router-dom';
import {
  clubDistanceKm,
  distanceEvents,
  duesState,
  financialYear,
  formatDate,
  formatMoney,
  outstandingTotal,
  payable,
  today,
} from '../../data';
import { useClubData } from '../../context/ClubContext';
import { useUI } from '../../context/UIContext';
import { useCountUp } from '../../hooks/useCountUp';
import { EventCard, trendingEventId } from '../../components/EventCard';
import { MemberSheet } from '../../components/sheets/MemberSheet';
import { MemberCreatedSheet } from '../../components/sheets/MemberCreatedSheet';
import { PayLinkSheet, RecordPaymentSheet } from '../../components/sheets/MoneySheets';
import {
  ClubSheetsQuickActions,
  DuesChaseTable,
  SafetyRows,
} from './DashboardParts';
import {
  Button,
  ButtonRow,
  Empty,
  Eyebrow,
  Reveal,
  Stat,
  Stats,
  ViewTitle,
} from '../../components/ui';

export function DashboardPage() {
  const { db } = useClubData();
  const { openSheet } = useUI();
  const navigate = useNavigate();

  const fy = financialYear(today());

  /* Every money and headcount figure on this screen is about the LIVE roster.
     A member who has left keeps their record forever (bibs are never reused)
     but owes nothing and is not one of "your members" — and a `pending`
     member is not on the roster YET: they live on Membership requests until
     the committee activates them, so their joining fee is pipeline, not
     outstanding dues. */
  const roster = db.members.filter((m) => m.status !== 'left' && m.status !== 'pending');

  const lapsed = roster.filter((m) => duesState(m).key === 'lapsed').length;
  const active = roster.length - lapsed;
  const collected = db.payments
    .filter((p) => financialYear(p.date) === fy)
    .reduce((sum, p) => sum + p.amount, 0);
  const outstanding = outstandingTotal(roster, db.meta.fees);

  /* The club's odometer. Counted per EVENT, not per runner: a 21K the club put
     on is 21 km whether twelve members finished it or five hundred. It moves
     only when the committee marks an event completed on its event page. */
  const doneEvents = distanceEvents(db.events);
  const km = useCountUp(Math.round(clubDistanceKm(db.events)));
  const money = useCountUp(collected);

  const pending = db.requests.filter((r) => r.status === 'pending');
  /* Paid, waiting only on the committee's final approval. These matter MORE
     than a new request — the money is already in and the person cannot sign
     in until someone here clicks Activate. Committee-added members walk the
     same pipeline, so a paid `pending` member counts here too. */
  const paidPendingMembers = db.members.filter(
    (m) =>
      m.status === 'pending' &&
      (m.duesPaidTill || db.payments.some((p) => p.memberId === m.id)),
  );
  const awaitingActivation = [
    ...db.requests.filter((r) => r.status === 'paid'),
    ...paidPendingMembers,
  ];

  /** Worst first: never-paid, then longest lapsed. */
  const chase = roster
    .filter(payable)
    .sort((a, b) => (a.duesPaidTill ?? '0').localeCompare(b.duesPaidTill ?? '0'))
    .slice(0, 6);

  const nextUp = db.events
    .filter((e) => e.date >= today())
    .sort((a, b) => a.date.localeCompare(b.date))
    .slice(0, 3);
  const hotEvent = trendingEventId(db.events, today());

  // Bibs are never reused, so this counts EVERY record, left members included.
  const nextBib = db.members.reduce((max, m) => Math.max(max, m.bib), 0) + 1;

  return (
    <>
      {/* The blocks settle in reading order on first paint. Priority runs top
          to bottom — requests waiting, then money owed, then what is coming —
          so the stagger reinforces the order rather than decorating it. */}
      {/* Which screen, and as-of when. Only below `lg` — on a desktop the rail
          already names the section, and the hero should open the page. */}
      <div className="mb-5 flex flex-wrap items-baseline justify-between gap-2 lg:hidden">
        <ViewTitle title="Dashboard" />
        <div className="font-market text-[11px] tracking-[0.08em] text-copy-label">
          FY {fy} · {formatDate(today())}
        </div>
      </div>

      {/* The hero and the four counters read as one band: the dark card is the
          club's headline number, the white cards are what needs attention. */}
      <div className="mb-3.5 grid gap-3.5 min-[900px]:grid-cols-[1.25fr_1fr]">
        <Reveal index={1}>
          <div className="relative h-full overflow-hidden bg-[linear-gradient(120deg,#161C2C,#0B0F18)] p-6 text-white">
            {/* Not the financial year. This is a running total that never
                resets — the club's odometer, exactly like a car's. The label
                used to say the FY beside an all-time figure. */}
            <div className="font-market text-[10px] font-bold uppercase tracking-[0.24em] text-gold-500">
              Club total · all time
            </div>
            <div className="mt-3 font-market text-[68px] font-extrabold leading-[0.85] tracking-[-0.04em] tabular-nums">
              {km.toLocaleString('en-IN')}
              {/* `tracking-normal` is load-bearing: the 68px figure above sets
                  -0.04em, and a nested label INHERITS it. At 24px that is
                  ~1px off every gap, which closes the word space and renders
                  this as "kmraced". A label at a different size from the
                  number it annotates must always set its own tracking. */}
              <small className="ml-2 font-market text-2xl font-bold tracking-normal text-white/45">
                km covered
              </small>
            </div>
            <div className="mt-3 max-w-[360px] font-market text-[13px] leading-[1.65] text-white/55">
              {doneEvents.length
                ? `Across ${doneEvents.length} completed event${doneEvents.length === 1 ? '' : 's'}. Mark an event completed and its distance lands here.`
                : 'Give an event a distance, then mark it completed once it has been run — its kilometres land here.'}
            </div>
            {/* the hi-vis lane marking, echoing the rule under the wordmark */}
            <div className="mt-6 h-[5px] bg-[linear-gradient(90deg,#F6C019,#7FD8C8)]" />
          </div>
        </Reveal>

        <Reveal index={2}>
          {/* Ink for the headline counts, colour only where a figure carries
              alarm — money owed in crimson, money in the bank in teal. The
              `gap-px` over `bg-line` is what draws the dividers: <Stat> has no
              border of its own, so a gapped grid would leave four white boxes
              floating on the ground with nothing separating them. */}
          <div className="grid h-full grid-cols-2 gap-px border border-line bg-line">
            {/* Each counter opens the list it counts (owner's direction,
                17 Aug 2026): members, the payment book, who owes, who lapsed. */}
            <Stat
              value={
                <>
                  {active}
                  <span className="text-line-strong">/{roster.length}</span>
                </>
              }
              label="Active members"
              onClick={() => navigate('/a/members?filter=active')}
            />
            <Stat
              numberTone="good"
              value={`₹${money.toLocaleString('en-IN')}`}
              label={`Collected · ${fy}`}
              onClick={() => navigate('/a/dues')}
            />
            <Stat
              numberTone={outstanding ? 'bad' : undefined}
              value={formatMoney(outstanding)}
              label="Outstanding"
              onClick={() => navigate('/a/dues?filter=outstanding')}
            />
            <Stat
              numberTone={lapsed ? 'flag' : undefined}
              value={lapsed}
              label="Dues lapsed"
              onClick={() => navigate('/a/dues?filter=lapsed')}
            />
          </div>
        </Reveal>
      </div>

      {/* Paid-and-waiting comes first and in emerald: their money is in and
          they cannot sign in until someone activates them. */}
      {awaitingActivation.length > 0 && (
        <Reveal index={3}>
          <div className="mt-3.5 flex flex-wrap items-center justify-between gap-3.5 border border-sea-300 bg-sea-50 px-4 py-3.5">
            <div>
              <div className="font-market text-[17px] font-extrabold tracking-[-0.02em] text-sea-600">
                {awaitingActivation.length} paid member
                {awaitingActivation.length > 1 ? 's' : ''} waiting to be activated
              </div>
              <div className="mt-1 font-market text-[10.5px] text-copy-muted">
                The money is in — they cannot sign in until you activate them
              </div>
            </div>
            <Button tone="p" onClick={() => navigate('/a/requests')}>
              Activate now
            </Button>
          </div>
        </Reveal>
      )}

      {pending.length > 0 && (
        <Reveal index={3}>
          <div className="mt-3.5 flex flex-wrap items-center justify-between gap-3.5 border border-flag-100 bg-flag-50 px-4 py-3.5">
            <div className="flex items-center gap-3.5">
              <span className="grid h-11 w-11 flex-none place-items-center bg-flag-500 font-market text-xl font-extrabold tabular-nums text-white">
                {pending.length}
              </span>
              <div>
                <div className="font-market text-[17px] font-extrabold tracking-[-0.02em] text-flag-600">
                  {pending.length} membership request{pending.length > 1 ? 's' : ''} waiting
                </div>
                <div className="mt-1 font-market text-[10.5px] text-copy-muted">
                  New runners asking to join the club
                </div>
              </div>
            </div>
            {/* crimson, not amber: the CTA belongs to the banner it sits in */}
            <Button
              tone="r"
              className="max-sm:w-full"
              onClick={() => navigate('/a/requests')}
            >
              Review requests →
            </Button>
          </div>
        </Reveal>
      )}

      {/* Actions sit under the alerts, not above them: what needs reacting to
          outranks what you might choose to do. */}
      <Reveal index={4}>
        <Eyebrow>Quick actions</Eyebrow>
        <ButtonRow>
          <Button
            tone="p"
            onClick={() =>
              openSheet(
                <MemberSheet
                  nextBib={nextBib}
                  /*
                   * The same follow-up the Members screen gives: what was
                   * created, where it went, and the payment link that is the
                   * next step. Without it this button dropped a member into
                   * Membership requests and said nothing about it.
                   */
                  onCreated={(id) => openSheet(<MemberCreatedSheet memberId={id} />)}
                />,
              )
            }
          >
            + Add member
          </Button>
          <ClubSheetsQuickActions db={db} />
        </ButtonRow>
      </Reveal>

      <Reveal index={5}>
        {/* The total rides alongside the heading — the list is only the worst
            six, so the number tells you the size of the real problem. */}
        <Eyebrow
          right={
            outstanding > 0 ? (
              <span className="tabular-nums text-flag-600">
                {formatMoney(outstanding)} outstanding · {roster.filter(payable).length} members
              </span>
            ) : null
          }
        >
          Dues to chase
        </Eyebrow>
        {chase.length ? (
          <DuesChaseTable
            members={chase}
            onOpen={(id) => navigate(`/a/members/${id}`)}
            onPayLink={(m) => openSheet(<PayLinkSheet member={m} />)}
            onCash={(m) => openSheet(<RecordPaymentSheet member={m} />)}
          />
        ) : (
          <Empty title="All paid up">Nothing to chase right now.</Empty>
        )}
      </Reveal>

      {/* What's coming and who to call sit side by side on a wide screen —
          they are reference, not action, so they share the closing band. */}
      <div className="grid gap-x-7 min-[1000px]:grid-cols-2">
        <Reveal index={5}>
          <Eyebrow>Next up</Eyebrow>
          {nextUp.length ? (
            nextUp.map((e) => (
              <EventCard
                key={e.id}
                event={e}
                db={db}
                meId={null}
                trending={e.id === hotEvent}
                onOpen={(id) => navigate(`/a/events/${id}`)}
              />
            ))
          ) : (
            <Empty>No upcoming events.</Empty>
          )}
        </Reveal>

        <Reveal index={6}>
          <Eyebrow>Club safety contact</Eyebrow>
          <SafetyRows db={db} />
        </Reveal>
      </div>
    </>
  );
}
